/**
 * Lo stile «Sinapsi» della pagina «Al timone» (contratto n. 1, fase 13): l'azienda come un cervello vivo. Ogni reparto è
 * un grappolo di neuroni (tanti quanti le persone, con un minimo per vederlo), ogni flusso di dati un impulso che corre;
 * al centro il timone. Si accende dove passa il cursore o il dito; con il movimento ridotto resta ferma e completa.
 */

export interface NetCluster {
  nome: string;
  persone: number | null;
  ore_mese: number | null;
}

export interface NetOptions {
  clusters: NetCluster[];
  /** Le etichette in alto (fonti), collegate al centro. */
  sorgenti: string[];
  centro: string;
  /** oggi = flussi di oggi; timone = con Timonio: impulsi doppi e ore liberate accese. */
  modo: 'oggi' | 'timone';
  /** Da 0 a 1: quanto la rete si è aperta dal punto di luce (la pianta viva la fa crescere). */
  apertura: number;
  /** Il centro della rete in proporzione alla tela; null = scelto dalla larghezza. */
  fuoco?: [number, number] | null;
  etichette?: boolean;
}

type Node = {
  x: number;
  y: number;
  z: number;
  r: number;
  c: number;
  head?: boolean;
  core?: boolean;
  src?: string;
};
type Pulse = { e: [number, number, boolean]; t: number; sp: number; col: string };

const CYAN = '60,240,255';
const VIOLET = '170,150,255';
const GOLD = '255,214,140';
const gauss = () => (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;

export class SinapsiNet {
  private ctx: CanvasRenderingContext2D;
  private W = 0;
  private H = 0;
  private nodes: Node[] = [];
  private edges: Array<[number, number, boolean]> = [];
  private pulses: Pulse[] = [];
  private raf = 0;
  private t0 = performance.now();
  private mouse = { x: -1e4, y: -1e4, on: false };
  private ro: ResizeObserver;
  private reduce =
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  private key = '';

  constructor(
    private canvas: HTMLCanvasElement,
    private o: NetOptions,
  ) {
    this.ctx = canvas.getContext('2d')!;
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(canvas);
    canvas.addEventListener('pointermove', this.onMove);
    canvas.addEventListener('pointerleave', this.onLeave);
    this.resize();
    this.raf = requestAnimationFrame(this.frame);
  }

  update(o: Partial<NetOptions>) {
    this.o = { ...this.o, ...o };
    const k = JSON.stringify([this.o.clusters.map((c) => [c.nome, c.persone]), this.o.sorgenti]);
    if (k !== this.key) this.build();
    if (this.reduce) this.draw(4000);
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    this.ro.disconnect();
    this.canvas.removeEventListener('pointermove', this.onMove);
    this.canvas.removeEventListener('pointerleave', this.onLeave);
  }

  private onMove = (e: PointerEvent) => {
    const r = this.canvas.getBoundingClientRect();
    this.mouse = { x: e.clientX - r.left, y: e.clientY - r.top, on: true };
  };
  private onLeave = () => {
    this.mouse = { x: -1e4, y: -1e4, on: false };
  };

  private resize() {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const r = this.canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    this.W = r.width;
    this.H = r.height;
    this.canvas.width = r.width * dpr;
    this.canvas.height = r.height * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.build();
    if (this.reduce) this.draw(4000);
  }

  private center(): [number, number] {
    if (this.o.fuoco) return [this.W * this.o.fuoco[0], this.H * this.o.fuoco[1]];
    return this.W > 860 ? [this.W * 0.66, this.H * 0.5] : [this.W * 0.5, this.H * 0.6];
  }

  private size() {
    return Math.min(this.W, this.H) * (this.W > 860 ? 0.34 : 0.3);
  }

  private build() {
    this.key = JSON.stringify([this.o.clusters.map((c) => [c.nome, c.persone]), this.o.sorgenti]);
    const S = this.size();
    const nodes: Node[] = [{ x: 0, y: 0, z: 0, r: 7, c: -1, core: true }];
    const edges: Array<[number, number, boolean]> = [];
    const cl = this.o.clusters.slice(0, 9);
    const heads: number[] = [];
    cl.forEach((c, i) => {
      const a = (i / Math.max(1, cl.length)) * Math.PI * 2 - Math.PI / 2;
      const cx = Math.cos(a) * S * 0.8;
      const cy = Math.sin(a) * S * 0.62;
      const cz = gauss() * S * 0.3;
      const h = nodes.length;
      heads.push(h);
      nodes.push({ x: cx, y: cy, z: cz, r: 4.2, c: i, head: true });
      edges.push([0, h, true]);
      const n = Math.min(60, 14 + 2 * (c.persone ?? 4));
      for (let k = 0; k < n; k++)
        nodes.push({
          x: cx + gauss() * S * 0.22,
          y: cy + gauss() * S * 0.18,
          z: cz + gauss() * S * 0.22,
          r: 0.8 + Math.random() * 1.2,
          c: i,
        });
    });
    this.o.sorgenti.slice(0, 6).forEach((s, i, all) => {
      nodes.push({
        x: (i - (all.length - 1) / 2) * S * 0.48,
        y: -S * 1.22,
        z: 0,
        r: 3.4,
        c: -2,
        src: s,
      });
      edges.push([nodes.length - 1, 0, true]);
    });
    // Le sinapsi: ogni neurone ai tre più vicini del suo reparto, qualcuno alla testa; le teste in cerchio.
    nodes.forEach((n, i) => {
      if (n.c < 0 || n.head) return;
      const near = nodes
        .map((m, j) => [j, m] as const)
        .filter(([j, m]) => j !== i && m.c === n.c)
        .sort((p, q) => d2(n, p[1]) - d2(n, q[1]))
        .slice(0, 3);
      near.forEach(([j]) => edges.push([i, j, false]));
      if (Math.random() < 0.22) edges.push([i, heads[n.c]!, false]);
    });
    heads.forEach(
      (h, k) => heads.length > 1 && edges.push([h, heads[(k + 1) % heads.length]!, true]),
    );
    this.nodes = nodes;
    this.edges = edges;
    this.pulses = [];
  }

  private frame = (now: number) => {
    this.draw(now - this.t0);
    if (!this.reduce) this.raf = requestAnimationFrame(this.frame);
  };

  private draw(t: number) {
    const { ctx, W, H } = this;
    if (!W || !H) return;
    const [cx, cy] = this.center();
    const S = this.size();
    const op = Math.max(0.02, Math.min(1, this.o.apertura));
    const rot = t * 0.00012 + (this.mouse.on ? (this.mouse.x / W - 0.5) * 0.6 : 0);
    const ca = Math.cos(rot);
    const sa = Math.sin(rot);
    const f = 900;
    ctx.fillStyle = '#02040a';
    ctx.fillRect(0, 0, W, H);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, S * 1.7);
    g.addColorStop(0, `rgba(${CYAN},${0.1 * op})`);
    g.addColorStop(0.5, `rgba(138,92,255,${0.06 * op})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    // La rete si apre dal punto di luce al centro.
    const P = this.nodes.map((n) => {
      const x = n.x * op * ca - n.z * op * sa;
      const z = n.x * op * sa + n.z * op * ca;
      const s = f / (f + z);
      return [cx + x * s, cy + n.y * op * s, s] as const;
    });
    ctx.globalCompositeOperation = 'lighter';
    for (const [i, j, big] of this.edges) {
      const p = P[i]!;
      const q = P[j]!;
      ctx.strokeStyle = big ? `rgba(${CYAN},${0.22 * op})` : `rgba(138,92,255,${0.16 * op})`;
      ctx.lineWidth = big ? 1 : 0.6;
      ctx.beginPath();
      ctx.moveTo(p[0], p[1]);
      ctx.lineTo(q[0], q[1]);
      ctx.stroke();
    }
    const rate = this.o.modo === 'timone' ? 1.1 : 0.45;
    if (!this.reduce && this.edges.length && Math.random() < rate * op) {
      const e = this.edges[(Math.random() * this.edges.length) | 0]!;
      this.pulses.push({
        e,
        t: 0,
        sp: e[2] ? 0.012 + Math.random() * 0.01 : 0.02 + Math.random() * 0.02,
        col:
          this.o.modo === 'timone' && Math.random() < 0.35
            ? '#ffd68c'
            : Math.random() < 0.5
              ? '#3cf0ff'
              : '#c9b5ff',
      });
    }
    this.pulses = this.pulses.filter((p) => (p.t += p.sp) < 1);
    for (const p of this.pulses) {
      const a = P[p.e[0]]!;
      const b = P[p.e[1]]!;
      const x = a[0] + (b[0] - a[0]) * p.t;
      const y = a[1] + (b[1] - a[1]) * p.t;
      const k = Math.max(0, p.t - 0.18);
      const tx = a[0] + (b[0] - a[0]) * k;
      const ty = a[1] + (b[1] - a[1]) * k;
      const gr = ctx.createLinearGradient(tx, ty, x, y);
      gr.addColorStop(0, 'rgba(0,0,0,0)');
      gr.addColorStop(1, p.col);
      ctx.strokeStyle = gr;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(tx, ty);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.fillStyle = p.col;
      ctx.beginPath();
      ctx.arc(x, y, 2.2, 0, 7);
      ctx.fill();
    }
    this.nodes.forEach((n, i) => {
      const [x, y, s] = P[i]!;
      const hot = Math.max(0, 1 - Math.hypot(x - this.mouse.x, y - this.mouse.y) / 120);
      const r = n.r * s * (1 + hot * 1.6) * (n.core ? 1 + Math.sin(t * 0.004) * 0.15 : 1);
      const col = n.src ? '255,255,255' : n.core ? GOLD : n.head ? CYAN : VIOLET;
      const gl = ctx.createRadialGradient(x, y, 0, x, y, r * 5);
      gl.addColorStop(0, `rgba(${col},${(0.5 + hot * 0.5) * op})`);
      gl.addColorStop(1, `rgba(${col},0)`);
      ctx.fillStyle = gl;
      ctx.beginPath();
      ctx.arc(x, y, r * 5, 0, 7);
      ctx.fill();
      ctx.fillStyle = `rgba(${col},${op})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, 7);
      ctx.fill();
    });
    ctx.globalCompositeOperation = 'source-over';
    // Il timone al centro.
    const [hx, hy] = P[0]!;
    ctx.save();
    ctx.translate(hx, hy);
    ctx.rotate(Math.sin(t * 0.0006) * 0.4);
    ctx.strokeStyle = `rgba(${GOLD},0.95)`;
    ctx.lineWidth = 2;
    ctx.shadowColor = '#ffd68c';
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.arc(0, 0, 22, 0, 7);
    ctx.stroke();
    for (let k = 0; k < 8; k++) {
      const a = (k * Math.PI) / 4;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * 6, Math.sin(a) * 6);
      ctx.lineTo(Math.cos(a) * 32, Math.sin(a) * 32);
      ctx.stroke();
    }
    ctx.restore();
    if (this.o.etichette === false || op < 0.6) return;
    ctx.textAlign = 'center';
    ctx.font = '600 11px "JetBrains Mono", ui-monospace, Menlo, monospace';
    ctx.fillStyle = `rgba(${GOLD},0.95)`;
    ctx.fillText(this.o.centro.toUpperCase(), hx, hy + 52);
    ctx.font = '500 10.5px "JetBrains Mono", ui-monospace, Menlo, monospace';
    this.nodes.forEach((n, i) => {
      if (!n.head && !n.src) return;
      const [x, y] = P[i]!;
      if (n.src) {
        ctx.fillStyle = 'rgba(234,240,255,0.7)';
        ctx.fillText(n.src.toUpperCase(), x, y - 12);
        return;
      }
      const c = this.o.clusters[n.c]!;
      ctx.fillStyle = `rgba(${CYAN},0.95)`;
      ctx.fillText(`${c.nome.toUpperCase()}${c.persone ? ` · ${c.persone}` : ''}`, x, y - 14);
      if (this.o.modo === 'timone' && c.ore_mese) {
        const txt = `+${Math.round(c.ore_mese)} h/mese`;
        const w = ctx.measureText(txt).width + 14;
        ctx.fillStyle = 'rgba(255,214,140,0.92)';
        ctx.beginPath();
        ctx.roundRect(x - w / 2, y + 10, w, 18, 9);
        ctx.fill();
        ctx.fillStyle = '#1a1407';
        ctx.fillText(txt, x, y + 23);
      }
    });
  }
}

const d2 = (a: Node, b: Node) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2;
